/**
 * Server configuration for bartender instances.
 *
 * Production uses a single URL - Fly.io's anycast routing automatically
 * routes users to the nearest region at the network level.
 */

const isDev = process.env.NODE_ENV === 'development'
const isPreview = process.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'

// Server URLs by environment
const SERVERS = {
  local: {
    apiUrl: 'http://localhost:3001',
    wsUrl: 'ws://localhost:3001/ws',
  },
  staging: {
    apiUrl: 'https://martini-bartender-staging.fly.dev',
    wsUrl: 'wss://martini-bartender-staging.fly.dev/ws',
  },
  production: {
    apiUrl: 'https://api.martini.film',
    wsUrl: 'wss://api.martini.film/ws',
  },
} as const

/**
 * Get the current server based on environment.
 * - Development: local
 * - Vercel preview: staging
 * - Production: production (Fly.io anycast handles geo-routing)
 */
function getCurrentServer() {
  if (isDev) return SERVERS.local
  if (isPreview) return SERVERS.staging
  return SERVERS.production
}

/**
 * Get current API URL.
 * Honours `NEXT_PUBLIC_API_URL_OVERRIDE` so a local prod build can point at a
 * local backend during perf profiling. The override is build-time-inlined and
 * has no effect unless explicitly set, so deployed builds are unaffected.
 */
export function getApiUrl(): string {
  const override = process.env.NEXT_PUBLIC_API_URL_OVERRIDE
  if (override) return override
  return getCurrentServer().apiUrl
}

/**
 * Get current MCP server URL — uses branded domain via Vercel rewrite
 */
export function getMcpServerUrl(): string {
  // Dedicated/enterprise deploys advertise their own branded MCP origin (e.g.
  // https://enterprise.martini.film/mcp). Build-time-inlined and unset in the
  // consumer prod/preview projects, so their behavior is unchanged.
  const override = process.env.NEXT_PUBLIC_MCP_URL_OVERRIDE
  if (override) return override
  if (isDev) {
    // `/mcp` is a same-origin Next rewrite to the backend, so the URL shown to users must be the
    // CURRENT app origin — under portless the app is served at https://<workspace>.localhost (not
    // :3000), and a hardcoded localhost:3000 would point at no server or a different concurrent
    // workspace. Falls back to :3000 during SSR (this is rendered in client UI).
    const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'
    return `${origin}/mcp`
  }
  if (isPreview) return `https://${process.env.NEXT_PUBLIC_VERCEL_URL}/mcp`
  return 'https://www.martini.film/mcp'
}

/**
 * Get current WebSocket URL.
 * Honours `NEXT_PUBLIC_WS_URL_OVERRIDE` for the same perf-profiling reason as
 * `getApiUrl()`.
 */
export function getWsUrl(): string {
  const override = process.env.NEXT_PUBLIC_WS_URL_OVERRIDE
  if (override) return override
  return getCurrentServer().wsUrl
}
